import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const UnverifiedUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { csrfToken } = useAuth();
    const [communities, setCommunities] = useState([]);

    const fetchUnverifiedUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/gl_admin/users/unverified');
            setUsers(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch unverified users: ' + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };

    const fetchCommunities = async () => {
        try {
            const response = await api.get('/communities');
            setCommunities(response.data.communities);
        } catch (err) {
        }
    };

    useEffect(() => {
        fetchUnverifiedUsers();
        fetchCommunities();
    }, []);

    const handleVerifyEmail = async (userId) => {
        try {
            await api.post(`/gl_admin/users/${userId}/verify-email`, {}, {
                headers: { 'X-CSRFToken': csrfToken }
            });
            fetchUnverifiedUsers(); // Refresh the user list
        } catch (err) {
            setError('Failed to verify email: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleApproveUser = async (userId) => {
        try {
            await api.post(`/gl_admin/users/${userId}/approve`, {}, {
                headers: { 'X-CSRFToken': csrfToken }
            });
            fetchUnverifiedUsers(); // Refresh the user list
        } catch (err) {
            setError('Failed to approve user: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleSendVerificationEmail = async (userId) => {
        try {
            await api.post(`/gl_admin/users/${userId}/send-verification`, {}, {
                headers: { 'X-CSRFToken': csrfToken }
            });
            alert('Verification email sent!');
        } catch (err) {
            setError('Failed to send verification email: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleAssignCommunity = async (userId, communityId) => {
        try {
            await api.post(`/gl_admin/users/${userId}/assign-community`, 
                { community_id: communityId }, 
                { headers: { 'X-CSRFToken': csrfToken } }
            );
            fetchUnverifiedUsers(); // Refresh the user list
        } catch (err) {
            setError('Failed to assign community: ' + (err.response?.data?.message || err.message));
        }
    };

    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" role="status"></div></div>;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div>
            <h2>Unverified Users</h2>
            {users.length === 0 ? (
                <div className="alert alert-info">No unverified users found.</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Email Verified</th>
                                <th>Approved</th>
                                <th>Community</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>{user.email}</td>
                                    <td>{user.display_name || user.email?.split('@')[0]}</td>
                                    <td>
                                        <span className={`badge ${user.email_verified ? 'bg-success' : 'bg-danger'}`}>
                                            {user.email_verified ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${user.is_approved ? 'bg-success' : 'bg-danger'}`}>
                                            {user.is_approved ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        {user.community_id ? 
                                            communities.find(c => c.id === user.community_id)?.name || user.community_id 
                                            : (
                                                <select 
                                                    className="form-select form-select-sm"
                                                    onChange={(e) => handleAssignCommunity(user.id, e.target.value)}
                                                >
                                                    <option value="">Assign Community</option>
                                                    {communities.map(community => (
                                                        <option key={community.id} value={community.id}>
                                                            {community.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                    </td>
                                    <td>
                                        {!user.email_verified && (
                                            <>
                                                <button 
                                                    className="btn btn-sm btn-success me-1"
                                                    onClick={() => handleVerifyEmail(user.id)}
                                                    title="Manually verify email"
                                                >
                                                    Verify Email
                                                </button>
                                                <button 
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    onClick={() => handleSendVerificationEmail(user.id)}
                                                    title="Send verification email again"
                                                >
                                                    Send Email
                                                </button>
                                            </>
                                        )}
                                        {!user.is_approved && (
                                            <button 
                                                className="btn btn-sm btn-warning"
                                                onClick={() => handleApproveUser(user.id)}
                                                disabled={!user.email_verified || !user.community_id}
                                                title={!user.email_verified || !user.community_id ? 
                                                    "Email must be verified and community assigned before approval" : 
                                                    "Approve user"}
                                            >
                                                Approve
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UnverifiedUsers;